import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../store';
import { SearchParams, SearchType, DateType } from '../../types';

const SearchForm: React.FC = () => {
  const { searchProjects, isAuthenticated } = useAppStore();
  
  const [formData, setFormData] = useState<SearchParams>({
    start_date: '',  // 默认为空，不限制时间范围
    end_date: '',    // 默认为空，不限制时间范围
    language: '',
    custom_keywords: 'ragflow',  // 默认搜索词改为项目名称
    search_type: 'name' as SearchType, // 默认按项目名称搜索
    date_type: 'created' as DateType, // 默认按创建时间搜索
    page: 1,
    per_page: 60
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    license: '',
    repo_size: '',
    min_stars: '',
    min_forks: '',
    last_update: '',
    has_readme: '',
    has_issues: '',
    sort_by: 'stars'
  });

  // 默认快捷标签
  const defaultTags = [
    'ai', 'ml', 'deep-learning', 'computer-vision', 'nlp',
    'transformer', 'pytorch', 'tensorflow', 'chatbot'
  ];

  // 用户自定义标签状态
  const [customTags, setCustomTags] = useState<string[]>(() => {
    const saved = localStorage.getItem('customSearchTags');
    return saved ? JSON.parse(saved) : [];
  });

  const [newTagInput, setNewTagInput] = useState('');
  const [showAddInput, setShowAddInput] = useState(false);
  const [showDeleteMode, setShowDeleteMode] = useState(false);
  const [showSearchTypeDropdown, setShowSearchTypeDropdown] = useState(false);

  // 合并默认标签和自定义标签
  const allTags = [...defaultTags, ...customTags];

  // 搜索类型选项
  const searchTypeOptions = [
    { value: 'name' as SearchType, label: '名称', icon: '' },
    { value: 'owner' as SearchType, label: '作者', icon: '' },
    { value: 'topic' as SearchType, label: '标签', icon: '' }
  ];

  // 移除自动设置默认日期，让用户自主选择是否使用时间范围

  // 点击外部区域关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.dropdown')) {
        setShowSearchTypeDropdown(false);
      }
    };

    if (showSearchTypeDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showSearchTypeDropdown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAdvancedChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setAdvancedFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 处理搜索类型变化
  const handleSearchTypeChange = (searchType: SearchType) => {
    setFormData(prev => ({
      ...prev,
      search_type: searchType
    }));
    setShowSearchTypeDropdown(false); // 选择后关闭下拉菜单
  };

  // 快捷日期设置
  const setQuickDate = (days: number) => {
    const today = new Date();
    const startDate = new Date(today.getTime() - days * 24 * 60 * 60 * 1000);

    setFormData(prev => ({
      ...prev,
      start_date: startDate.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0]
    }));
  };

  // 清除时间范围
  const clearDateRange = () => {
    setFormData(prev => ({
      ...prev,
      start_date: '',
      end_date: ''
    }));
  };

  // 添加快捷标签到搜索框
  const addQuickTag = (tag: string) => {
    const currentKeywords = formData.custom_keywords;
    const keywords = currentKeywords ? currentKeywords.split(',').map(k => k.trim()) : [];

    if (!keywords.includes(tag)) {
      keywords.push(tag);
      setFormData(prev => ({
        ...prev,
        custom_keywords: keywords.join(', ')
      }));
    }
  };

  // 添加新的自定义标签
  const addCustomTag = () => {
    const tag = newTagInput.trim().toLowerCase();
    if (tag && !allTags.includes(tag)) {
      const newCustomTags = [...customTags, tag];
      setCustomTags(newCustomTags);
      localStorage.setItem('customSearchTags', JSON.stringify(newCustomTags));
      setNewTagInput('');
      setShowAddInput(false);
    }
  };

  // 删除自定义标签
  const removeCustomTag = (tagToRemove: string) => {
    const newCustomTags = customTags.filter(tag => tag !== tagToRemove);
    setCustomTags(newCustomTags);
    localStorage.setItem('customSearchTags', JSON.stringify(newCustomTags));
  };

  // 处理添加标签的键盘事件
  const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomTag();
    } else if (e.key === 'Escape') {
      setShowAddInput(false);
      setNewTagInput('');
    }
  };

  // 切换删除模式
  const toggleDeleteMode = () => {
    setShowDeleteMode(!showDeleteMode);
    if (showAddInput) {
      setShowAddInput(false);
      setNewTagInput('');
    }
  };

  // 切换添加模式
  const toggleAddMode = () => {
    setShowAddInput(!showAddInput);
    if (showDeleteMode) {
      setShowDeleteMode(false);
    }
    if (!showAddInput) {
      setNewTagInput('');
    }
  };

  // 清空关键词
  const clearKeywords = () => {
    setFormData(prev => ({
      ...prev,
      custom_keywords: ''
    }));
  };

  // 重置高级过滤器
  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      license: '',
      repo_size: '',
      min_stars: '',
      min_forks: '',
      last_update: '',
      has_readme: '',
      has_issues: '',
      sort_by: 'stars'
    });
  };

  // 保存搜索预设
  const saveSearchPreset = () => {
    // TODO: 实现保存搜索预设功能
    console.log('保存搜索预设');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // 合并基础参数和高级过滤器
    const searchParams = {
      ...formData,
      ...advancedFilters
    };

    await searchProjects(searchParams, isAuthenticated);
  };

  return (
    <div className="search-card">
      <form onSubmit={handleSubmit}>
        {/* 主搜索框区域 */}
        <div className="main-search-section">
              <div className="row g-3 align-items-end">
                <div className="col-md-8">
                  <div className="search-input-container">
                    <div className="input-group input-group-lg">
                      {/* 搜索类型下拉菜单 */}
                      <div className="dropdown">
                        <button
                          className="btn btn-outline-secondary btn-lg dropdown-toggle search-type-dropdown"
                          type="button"
                          onClick={() => setShowSearchTypeDropdown(!showSearchTypeDropdown)}
                          aria-expanded={showSearchTypeDropdown}
                          title="选择搜索类型"
                        >
                          <i className={searchTypeOptions.find(opt => opt.value === formData.search_type)?.icon}></i>
                          <span className="search-type-label">
                            {searchTypeOptions.find(opt => opt.value === formData.search_type)?.label}
                          </span>
                        </button>
                        <ul className={`dropdown-menu ${showSearchTypeDropdown ? 'show' : ''}`}>
                          {searchTypeOptions.map(option => (
                            <li key={option.value}>
                              <button
                                className={`dropdown-item ${formData.search_type === option.value ? 'active' : ''}`}
                                type="button"
                                onClick={() => handleSearchTypeChange(option.value)}
                              >
                                <i className={option.icon}></i>
                                <span>{option.label}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* 搜索输入框 */}
                      <input
                        type="text"
                        className="form-control form-control-lg search-input-with-clear"
                        id="customKeywords"
                        name="custom_keywords"
                        value={formData.custom_keywords}
                        onChange={handleInputChange}
                        placeholder={`搜索GitHub项目，例如: ${formData.search_type === 'topic' ? 'ai, machine learning, react' : formData.search_type === 'name' ? 'tensorflow, pytorch, react' : 'facebook, google, microsoft'}...`}
                        autoComplete="off"
                      />

                      {/* 清空按钮 */}
                      {formData.custom_keywords && (
                        <button
                          type="button"
                          className="search-clear-btn"
                          onClick={clearKeywords}
                          title="清空搜索词"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>

                    <div className="search-suggestions" id="searchSuggestions">
                      {/* 搜索建议将在这里显示 */}
                    </div>
                  </div>
                </div>
                <div className="col-md-2">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg w-100"
                  >
                    <i className="fas fa-search"></i> 搜索
                  </button>
                </div>
                <div className="col-md-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-lg w-100"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                  >
                    <i className={`fas fa-chevron-${showAdvanced ? 'up' : 'down'}`}></i>
                    高级搜索
                  </button>
                </div>
              </div>
            </div>

            {/* 热门搜索标签 - 只在标签搜索模式下显示 */}
            {formData.search_type === 'topic' && (
              <div className="row mt-4">
                <div className="col-12">
                  <label className="form-label fw-bold">热门搜索标签</label>
                <div className="quick-tags-container">
                  <div className="quick-tags" id="quickTags">
                    {/* 默认标签 */}
                    {defaultTags.map((tag, index) => (
                      <span
                        key={`default-${index}`}
                        className={`quick-tag quick-tag-default ${showDeleteMode ? 'delete-mode' : ''}`}
                        onClick={() => !showDeleteMode && addQuickTag(tag)}
                        title={showDeleteMode ? "默认标签不能删除" : "点击添加到搜索"}
                      >
                        {tag}
                        {showDeleteMode && (
                          <span className="tag-delete-indicator disabled">
                            <i className="fas fa-ban"></i>
                          </span>
                        )}
                      </span>
                    ))}

                    {/* 自定义标签 */}
                    {customTags.map((tag, index) => (
                      <span
                        key={`custom-${index}`}
                        className={`quick-tag quick-tag-custom ${showDeleteMode ? 'delete-mode' : ''}`}
                        onClick={() => !showDeleteMode && addQuickTag(tag)}
                        title={showDeleteMode ? "点击删除标签" : "点击添加到搜索"}
                      >
                        {tag}
                        {showDeleteMode && (
                          <button
                            type="button"
                            className="tag-delete-indicator"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeCustomTag(tag);
                            }}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        )}
                      </span>
                    ))}

                    {/* 控制按钮 */}
                    <div className="tag-controls">
                      {/* 删除模式按钮 */}
                      <button
                        type="button"
                        className={`tag-control-btn delete-btn ${showDeleteMode ? 'active' : ''}`}
                        onClick={toggleDeleteMode}
                        title={showDeleteMode ? "退出删除模式" : "删除标签"}
                      >
                        <i className="fas fa-minus"></i>
                      </button>

                      {/* 添加模式按钮 */}
                      <button
                        type="button"
                        className={`tag-control-btn add-btn ${showAddInput ? 'active' : ''}`}
                        onClick={toggleAddMode}
                        title={showAddInput ? "取消添加" : "添加标签"}
                      >
                        <i className="fas fa-plus"></i>
                      </button>

                      {/* 添加标签输入框 */}
                      {showAddInput && (
                        <div className="add-tag-input">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="输入标签名..."
                            value={newTagInput}
                            onChange={(e) => setNewTagInput(e.target.value)}
                            onKeyDown={handleTagInputKeyPress}
                            autoFocus
                          />
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={addCustomTag}
                            disabled={!newTagInput.trim()}
                          >
                            <i className="fas fa-check"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  </div>
                </div>
              </div>
            )}

            {/* 高级搜索面板 */}
            {showAdvanced && (
              <div className="advanced-search-panel" id="advancedSearchPanel">
                {/* 搜索时间范围和编程语言 - 移动到高级搜索面板顶部 */}
                <div className="row g-3">
                  {/* 时间类型选择 */}
                  <div className="col-md-3">
                    <label htmlFor="date_type" className="form-label">
                      <i className="fas fa-calendar-alt me-1"></i>
                      时间类型
                    </label>
                    <select
                      className="form-select"
                      name="date_type"
                      value={formData.date_type || 'created'}
                      onChange={handleInputChange}
                    >
                      <option value="created">📅 项目创建时间</option>
                      <option value="pushed">🔄 最新更新时间</option>
                      <option value="updated">✏️ 项目信息更新时间</option>
                    </select>
                    <div className="form-text">
                      {formData.date_type === 'created' && '可选：按项目首次创建的时间筛选'}
                      {formData.date_type === 'pushed' && '可选：按项目最后一次代码推送时间筛选'}
                      {formData.date_type === 'updated' && '可选：按项目信息最后更新时间筛选'}
                      <br />
                      <small className="text-muted">💡 不设置日期范围将搜索所有时间的项目</small>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="start_date" className="form-label">
                      {formData.date_type === 'created' && '📅 创建开始日期'}
                      {formData.date_type === 'pushed' && '🔄 更新开始日期'}
                      {formData.date_type === 'updated' && '✏️ 信息更新开始日期'}
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleInputChange}
                      placeholder="可选：设置开始日期"
                    />
                    <div className="form-text">可选：留空表示不限制时间范围</div>
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="end_date" className="form-label">
                      {formData.date_type === 'created' && '📅 创建结束日期'}
                      {formData.date_type === 'pushed' && '🔄 更新结束日期'}
                      {formData.date_type === 'updated' && '✏️ 信息更新结束日期'}
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleInputChange}
                      placeholder="可选：设置结束日期"
                    />
                    <div className="form-text">可选：留空表示不限制时间范围</div>
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="language" className="form-label">编程语言</label>
                    <select
                      className="form-select"
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                    >
                      <option value="">所有语言</option>
                      <option value="Python">Python</option>
                      <option value="JavaScript">JavaScript</option>
                      <option value="TypeScript">TypeScript</option>
                      <option value="Java">Java</option>
                      <option value="C++">C++</option>
                      <option value="Go">Go</option>
                      <option value="Rust">Rust</option>
                    </select>
                  </div>
                </div>

                {/* 快捷时间选择 */}
                <div className="row mt-3">
                  <div className="col-12">
                    <label className="form-label">
                      <i className="fas fa-clock me-1"></i>
                      快捷时间设置
                    </label>
                    <div className="quick-date-buttons">
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setQuickDate(7)}>
                        📅 最近7天
                      </button>
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setQuickDate(30)}>
                        📅 最近30天
                      </button>
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setQuickDate(90)}>
                        📅 最近90天
                      </button>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearDateRange}>
                        🚫 清除时间限制
                      </button>
                    </div>
                    <div className="form-text">
                      💡 点击快捷按钮设置时间范围，或点击"清除时间限制"搜索所有时间的项目
                    </div>
                  </div>
                </div>

                <div className="row mt-3 g-3">
                  {/* 许可证类型 */}
                  <div className="col-md-3">
                    <label htmlFor="license" className="form-label">许可证类型</label>
                    <select className="form-select" name="license" value={advancedFilters.license} onChange={handleAdvancedChange}>
                      <option value="">所有许可证</option>
                      <option value="mit">MIT</option>
                      <option value="apache-2.0">Apache 2.0</option>
                      <option value="gpl-3.0">GPL 3.0</option>
                      <option value="bsd-3-clause">BSD 3-Clause</option>
                      <option value="unlicense">Unlicense</option>
                      <option value="isc">ISC</option>
                      <option value="lgpl-3.0">LGPL 3.0</option>
                    </select>
                  </div>

                  {/* 仓库大小 */}
                  <div className="col-md-3">
                    <label htmlFor="repoSize" className="form-label">仓库大小 (KB)</label>
                    <select className="form-select" name="repo_size" value={advancedFilters.repo_size} onChange={handleAdvancedChange}>
                      <option value="">任意大小</option>
                      <option value="0..1000">小型 (&lt; 1MB)</option>
                      <option value="1000..10000">中型 (1-10MB)</option>
                      <option value="10000..100000">大型 (10-100MB)</option>
                      <option value="100000..*">超大型 (&gt; 100MB)</option>
                    </select>
                  </div>

                  {/* 最小Star数 */}
                  <div className="col-md-3">
                    <label htmlFor="minStars" className="form-label">最小Star数</label>
                    <select className="form-select" name="min_stars" value={advancedFilters.min_stars} onChange={handleAdvancedChange}>
                      <option value="">不限制</option>
                      <option value="10">10+</option>
                      <option value="50">50+</option>
                      <option value="100">100+</option>
                      <option value="500">500+</option>
                      <option value="1000">1000+</option>
                      <option value="5000">5000+</option>
                    </select>
                  </div>

                  {/* 最小Fork数 */}
                  <div className="col-md-3">
                    <label htmlFor="minForks" className="form-label">最小Fork数</label>
                    <select className="form-select" name="min_forks" value={advancedFilters.min_forks} onChange={handleAdvancedChange}>
                      <option value="">不限制</option>
                      <option value="5">5+</option>
                      <option value="10">10+</option>
                      <option value="25">25+</option>
                      <option value="50">50+</option>
                      <option value="100">100+</option>
                      <option value="500">500+</option>
                    </select>
                  </div>
                </div>

                <div className="row mt-3 g-3">
                  {/* 最后更新时间 */}
                  <div className="col-md-3">
                    <label htmlFor="lastUpdate" className="form-label">最后更新</label>
                    <select className="form-select" name="last_update" value={advancedFilters.last_update} onChange={handleAdvancedChange}>
                      <option value="">任意时间</option>
                      <option value="1">1天内</option>
                      <option value="7">1周内</option>
                      <option value="30">1个月内</option>
                      <option value="90">3个月内</option>
                      <option value="365">1年内</option>
                    </select>
                  </div>

                  {/* 是否有README */}
                  <div className="col-md-3">
                    <label htmlFor="hasReadme" className="form-label">文档完整性</label>
                    <select className="form-select" name="has_readme" value={advancedFilters.has_readme} onChange={handleAdvancedChange}>
                      <option value="">不限制</option>
                      <option value="true">有README</option>
                      <option value="wiki">有Wiki</option>
                      <option value="pages">有GitHub Pages</option>
                    </select>
                  </div>

                  {/* 是否有Issues */}
                  <div className="col-md-3">
                    <label htmlFor="hasIssues" className="form-label">社区活跃度</label>
                    <select className="form-select" name="has_issues" value={advancedFilters.has_issues} onChange={handleAdvancedChange}>
                      <option value="">不限制</option>
                      <option value="true">开启Issues</option>
                      <option value="discussions">有Discussions</option>
                      <option value="good-first-issues">有新手友好Issues</option>
                    </select>
                  </div>

                  {/* 排序方式 */}
                  <div className="col-md-3">
                    <label htmlFor="sortBy" className="form-label">排序方式</label>
                    <select className="form-select" name="sort_by" value={advancedFilters.sort_by} onChange={handleAdvancedChange}>
                      <option value="stars">按Star数</option>
                      <option value="updated">按更新时间</option>
                      <option value="created">按创建时间</option>
                      <option value="forks">按Fork数</option>
                      <option value="help-wanted-issues">按求助Issues</option>
                    </select>
                  </div>
                </div>

                {/* 高级搜索操作按钮 */}
                <div className="row mt-3">
                  <div className="col-12">
                    <div className="advanced-search-actions">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={resetAdvancedFilters}
                      >
                        <i className="fas fa-undo"></i> 重置过滤器
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-info btn-sm"
                        onClick={saveSearchPreset}
                      >
                        <i className="fas fa-save"></i> 保存搜索预设
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
      </form>
    </div>
  );
};

export default SearchForm;
